/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useCallback, useEffect, useMemo } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';

import Spinner from '@atlaskit/spinner';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import PipelinesEditor, {
  VariablesProps,
} from 'src/components/pipelines/components/editor';
import { requestDeploymentDashboard } from 'src/components/pipelines/redux/actions/deployments';
import { setValidator } from 'src/components/pipelines/redux/actions/pipelines';
import {
  requestCreateEnvironmentVariable,
  requestCreateVariable,
  requestDeleteEnvironmentVariable,
  requestDeleteVariable,
  requestEnvironmentVariables,
  requestUpdateEnvironmentVariable,
  requestUpdateVariable,
  requestVariables,
} from 'src/components/pipelines/redux/actions/variables';
import {
  getDeploymentDashboardFetchedStatus,
  getEditorEnvironments,
  getEnvironmentsByUuid,
} from 'src/components/pipelines/redux/selectors/deployments';
import {
  getEnvironmentVariables,
  getEnvironmentVariablesFetchState,
  getRepository,
  getValidator,
  getVariables,
  getVariablesFetchedStatus,
} from 'src/components/pipelines/redux/selectors/pipelines';
import { LoadingStatus } from 'src/constants/loading-status';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useConfigNumber } from 'src/contexts/statsig-config-provider';
import { getCurrentRepository } from 'src/selectors/repository-selectors';

import { MAX_STEP_DURATION_IN_MINUTES } from '../../constants';

const Loading = styled.div({
  width: '100%',
  textAlign: 'center',
  padding: '100px 0 100px',
});

export function useEnvironments() {
  const dispatch = useDispatch();

  const editorEnvironments = useSelector(getEditorEnvironments);
  const fetchStatus = useSelector(getDeploymentDashboardFetchedStatus);
  const currentRepository = useSelector(getCurrentRepository);

  useEffect(() => {
    if (currentRepository?.full_name && fetchStatus === LoadingStatus.Before) {
      dispatch(requestDeploymentDashboard());
    }
  }, [currentRepository?.full_name, dispatch, fetchStatus]);

  return {
    editorEnvironments,
    isFetchingEnvironments:
      fetchStatus === LoadingStatus.Before ||
      fetchStatus === LoadingStatus.Fetching,
  };
}

export function useVariables() {
  const dispatch = useDispatch();
  const repository = useSelector(getRepository);
  const currentRepository = useSelector(getCurrentRepository);

  const variables = useSelector(getVariables);
  const variablesFetchedStatus = useSelector(getVariablesFetchedStatus);

  const environmentsByUuid = useSelector(getEnvironmentsByUuid);
  const environmentVariables = useSelector(getEnvironmentVariables);
  const environmentVariablesFetchState = useSelector(
    getEnvironmentVariablesFetchState
  );

  useEffect(() => {
    if (
      currentRepository?.full_name &&
      variablesFetchedStatus === LoadingStatus.Before
    ) {
      dispatch(requestVariables());
    }
  }, [currentRepository?.full_name, dispatch, variablesFetchedStatus]);

  useEffect(() => {
    if (currentRepository?.full_name) {
      Object.keys(environmentsByUuid).forEach(envUuid => {
        if (environmentVariablesFetchState[envUuid] === undefined) {
          dispatch(requestEnvironmentVariables(envUuid));
        }
      });
    }
  }, [
    currentRepository?.full_name,
    dispatch,
    environmentVariablesFetchState,
    environmentsByUuid,
  ]);

  const createVariable = useCallback(
    (variable: any, environmentUuid: any) => {
      dispatch(
        environmentUuid
          ? requestCreateEnvironmentVariable(environmentUuid, variable)
          : requestCreateVariable(variable)
      );
    },
    [dispatch]
  );

  const updateVariable = useCallback(
    (prevVariable: any, variable: any, environmentUuid: any) => {
      dispatch(
        environmentUuid
          ? requestUpdateEnvironmentVariable(
              environmentUuid,
              prevVariable,
              variable
            )
          : requestUpdateVariable(prevVariable, variable)
      );
    },
    [dispatch]
  );

  const deleteVariable = useCallback(
    (variable: any, envUuid: any) => {
      const index = (
        envUuid ? environmentVariables[envUuid] : variables
      )?.findIndex(v => v.uuid === variable.uuid);
      dispatch(
        envUuid
          ? requestDeleteEnvironmentVariable(envUuid, variable, index)
          : requestDeleteVariable(variable, index)
      );
    },
    [dispatch, environmentVariables, variables]
  );

  const variableKeys = useMemo(() => {
    return variables.map(v => v.key);
  }, [variables]);

  const variablesProps = useMemo<VariablesProps>(() => {
    const isFetchingVariables =
      variablesFetchedStatus === LoadingStatus.Before ||
      variablesFetchedStatus === LoadingStatus.Fetching ||
      Object.keys(environmentsByUuid).some(envUuid => {
        const envFetchStatus = environmentVariablesFetchState[envUuid];
        return (
          envFetchStatus === LoadingStatus.Before ||
          envFetchStatus === LoadingStatus.Fetching
        );
      });
    const environments: Record<string, string> = {};
    Object.entries(environmentsByUuid).forEach(([uuid, env]) => {
      environments[uuid] = env.name;
    });
    return {
      createVariable,
      deleteVariable,
      environmentVariables,
      environments,
      isFetchingVariables,
      isReadOnly: !repository?.userIsAdmin,
      updateVariable,
      variables,
    };
  }, [
    createVariable,
    deleteVariable,
    environmentVariables,
    environmentVariablesFetchState,
    environmentsByUuid,
    repository?.userIsAdmin,
    updateVariable,
    variables,
    variablesFetchedStatus,
  ]);

  return { variableKeys, variablesProps };
}

function useEditorFeatureFlags() {
  const maximumAllowedStepSizeForCloudRunners = useConfigNumber(
    StatsigFeatureKeys.pipelinesMaximumAllowedStepSizeForCloudRunners,
    2
  );

  const excludePathsChangesetConditionEnabled = useStatsigGate(
    StatsigFeatureKeys.isExcludePathsChangesetConditionEnabled
  );

  const isNewArtifactConfigEnabled = useStatsigGate(
    StatsigFeatureKeys.isNewArtifactConfigEnabled
  );

  return {
    effectiveVariablesInYamlEnabled: false, // not used anymore
    excludePathsChangesetConditionEnabled,
    isNewArtifactConfigEnabled,
    maxStepDuration: MAX_STEP_DURATION_IN_MINUTES,
    maximumAllowedStepSizeForCloudRunners,
  };
}

type Props = {
  isLoading?: boolean;
  onChange?: Function;
};

export const Editor = (props: Props) => {
  const dispatch = useDispatch();
  const repository = useSelector(getRepository);

  const validator = useSelector(getValidator);
  const { editorEnvironments, isFetchingEnvironments } = useEnvironments();
  const { variableKeys, variablesProps } = useVariables();
  const { ...featureFlags } = useEditorFeatureFlags();

  const onChange = useCallback(
    ({ code, isValid }: any) => {
      if (code !== validator.code || isValid !== validator.isValid) {
        dispatch(setValidator({ code, template: '', isValid }));
        props.onChange?.({ code, isValid });
      }
    },
    [dispatch, props, validator.code, validator.isValid]
  );

  const isLoading =
    props.isLoading ||
    !repository?.hasFetchedUserIsAdmin ||
    isFetchingEnvironments;

  return isLoading ? (
    <Loading>
      <Spinner delay={500} size="large" />
    </Loading>
  ) : (
    <PipelinesEditor
      code={validator.code}
      environments={editorEnvironments}
      onChange={onChange}
      variables={variableKeys}
      variablesProps={variablesProps}
      {...featureFlags}
    />
  );
};
